import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Scan, Camera, CreditCard, Usb, ArrowLeft, X } from 'lucide-react';
import axios from 'axios';
import { parseScannedId, normalizeGuestData } from '../utils/idScanner';

const API = process.env.REACT_APP_BACKEND_URL;

const IDScannerModal = ({ open, onClose, onDataScanned }) => {
  const [step, setStep] = useState('select');
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [scannedText, setScannedText] = useState('');
  const [scanStatus, setScanStatus] = useState('');
  const [barcodeScanningAvailable, setBarcodeScanningAvailable] = useState(true);
  const [isSending, setIsSending] = useState(false);

  const videoRef = useRef(null);
  const inputRef = useRef(null);
  const timeoutRef = useRef(null);
  const mountedRef = useRef(true);
  const streamRef = useRef(null);
  const cleaningUpRef = useRef(false);

  const stopCamera = useCallback(() => {
    // Stop tracks via streamRef (works even after unmount when videoRef is null)
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => {
        try { t.stop(); } catch (_) {}
      });
      streamRef.current = null;
    }
    // Also clear the video element if still available
    try {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch (_) {}
  }, []);

  const cleanup = useCallback(() => {
    if (cleaningUpRef.current) return;
    cleaningUpRef.current = true;
    stopCamera();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setStep('select');
    setSelectedMethod(null);
    setScannedText('');
    setScanStatus('');
    setIsSending(false);
    // Reset guard after state updates are queued
    setTimeout(() => { cleaningUpRef.current = false; }, 50);
  }, [stopCamera]);

  // Handle open/close
  useEffect(() => {
    mountedRef.current = true;
    if (open) {
      checkBarcodeScanningStatus();
    } else {
      cleanup();
    }
    return () => { mountedRef.current = false; };
  }, [open, cleanup]);

  // Unmount cleanup — ensures camera is ALWAYS stopped
  useEffect(() => {
    return () => {
      mountedRef.current = false;
      stopCamera();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [stopCamera]);

  const checkBarcodeScanningStatus = async () => {
    try {
      const res = await axios.get(`${API}/api/id-scanning/status`);
      if (mountedRef.current) {
        setBarcodeScanningAvailable(res.data?.status === 'operational');
      }
    } catch {
      if (mountedRef.current) setBarcodeScanningAvailable(true);
    }
  };

  const startCamera = async () => {
    setScanStatus('Starting camera...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1080 } }
      });
      // If component unmounted or modal closed while waiting for camera
      if (!mountedRef.current || !open) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
      setScanStatus('');
    } catch (err) {
      if (mountedRef.current) {
        setScanStatus('Camera access denied.');
        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) {
            setStep('select');
            setSelectedMethod(null);
            setScanStatus('');
          }
        }, 2000);
      }
    }
  };

  const captureAndDecode = async () => {
    if (!videoRef.current || isSending) return;
    const vw = videoRef.current.videoWidth;
    const vh = videoRef.current.videoHeight;
    if (!vw || !vh) return;

    setIsSending(true);
    setScanStatus('Processing...');

    try {
      const canvas = document.createElement('canvas');
      canvas.width = vw;
      canvas.height = vh;
      canvas.getContext('2d').drawImage(videoRef.current, 0, 0, vw, vh);

      const blob = await new Promise(r => canvas.toBlob(r, 'image/jpeg', 0.92));
      const formData = new FormData();
      formData.append('file', blob, 'id-capture.jpg');

      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/id-scanning/decode-barcode`,
        formData,
        {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
          timeout: 15000
        }
      );

      if (!mountedRef.current) return;

      if (response.data.success && response.data.data) {
        const d = response.data.data;
        // Stop camera FIRST, then notify parent
        stopCamera();
        onDataScanned({
          fullName: d.fullName,
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          documentNumber: d.documentNumber,
          documentType: d.documentType,
          dateOfBirth: d.dateOfBirth,
          addressLine1: d.addressLine1,
          city: d.city,
          state: d.state,
          postalCode: d.postalCode,
          country: d.country
        });
        // Let parent close the modal — don't call cleanup here
        onClose();
      } else {
        setScanStatus('No barcode found — tilt ID to reduce glare and try again');
        setIsSending(false);
        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) setScanStatus('');
        }, 3000);
      }
    } catch (err) {
      if (mountedRef.current) {
        setScanStatus('Error — try again');
        setIsSending(false);
        timeoutRef.current = setTimeout(() => {
          if (mountedRef.current) setScanStatus('');
        }, 3000);
      }
    }
  };

  const handleMethodSelect = (method) => {
    setSelectedMethod(method);
    setStep('scanning');
    setScannedText('');
    setScanStatus('');
    if (method === 'camera') {
      setTimeout(() => startCamera(), 300);
    } else {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setScannedText(value);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    // D5100/USB scanners type fast; 1200ms timeout to ensure all data arrives
    const delay = selectedMethod === 'magstripe' ? 1000 : 1200;
    timeoutRef.current = setTimeout(() => processScannedData(value), delay);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      // Use the DOM input value directly to avoid stale React state closure
      const currentValue = inputRef.current?.value || scannedText;
      timeoutRef.current = setTimeout(() => processScannedData(currentValue), 300);
    }
    if (e.key === 'Escape') {
      stopCamera();
      onClose();
    }
  };

  const processScannedData = async (rawData) => {
    if (!rawData || rawData.trim().length < 10) return;

    setScanStatus('Parsing ID data...');

    // Try backend parsing first (more robust for AAMVA)
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API}/api/id-scanning/parse-text`,
        { raw_text: rawData },
        {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          timeout: 5000
        }
      );
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        onDataScanned({
          fullName: d.fullName,
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          documentNumber: d.documentNumber,
          documentType: d.documentType || 'DRIVERS_LICENSE',
          dateOfBirth: d.dateOfBirth,
          addressLine1: d.addressLine1,
          city: d.city,
          state: d.state,
          postalCode: d.postalCode,
          country: d.country
        });
        onClose();
        return;
      }
    } catch (err) {
      console.log('[IDScanner] Backend parse failed, trying frontend parser:', err.message);
    }

    // Fallback: frontend parsing
    const parsed = parseScannedId(rawData);
    if (parsed) {
      onDataScanned(normalizeGuestData(parsed));
      onClose();
    } else {
      setScanStatus('Could not parse ID data. Try again or enter manually.');
      timeoutRef.current = setTimeout(() => {
        if (mountedRef.current) {
          setScanStatus('');
          setStep('select');
          setSelectedMethod(null);
          setScannedText('');
        }
      }, 3000);
    }
  };

  const handleBack = () => {
    stopCamera();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setStep('select');
    setSelectedMethod(null);
    setScannedText('');
    setScanStatus('');
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) { stopCamera(); onClose(); } }}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scan className="text-cyan-600" size={20} />
            Scan ID
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          {step === 'select' && (
            <div className="py-2">
              <p className="text-center text-slate-700 font-medium mb-2 text-sm">Select Scanning Method</p>

              <div className="mb-3 p-2 bg-cyan-50 border border-cyan-300 rounded">
                <p className="text-xs text-cyan-900 font-bold">Recommended: Tera D5100 or Mag Stripe Reader</p>
                <p className="text-xs text-cyan-800">Fastest &amp; most reliable — just scan or swipe!</p>
              </div>

              {!barcodeScanningAvailable && (
                <div className="mb-3 p-2 bg-amber-50 border border-amber-300 rounded">
                  <p className="text-xs text-amber-900 font-bold">Camera Barcode Scanning Unavailable</p>
                  <p className="text-xs text-amber-800">Please use Tera D5100, Mag Stripe Reader, or USB Scanner.</p>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <button
                  onClick={() => handleMethodSelect('d5100')}
                  className="border-2 border-cyan-300 bg-cyan-50 rounded-lg p-4 hover:border-cyan-500 hover:bg-cyan-100 transition-all text-center"
                >
                  <Scan className="mx-auto text-cyan-600 mb-2" size={32} />
                  <p className="font-medium text-slate-900 text-sm">Tera D5100</p>
                  <p className="text-xs text-cyan-700">Barcode</p>
                </button>
                <button
                  onClick={() => handleMethodSelect('magstripe')}
                  className="border-2 border-slate-200 rounded-lg p-4 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-center"
                >
                  <CreditCard className="mx-auto text-cyan-600 mb-2" size={32} />
                  <p className="font-medium text-slate-900 text-sm">Mag Stripe</p>
                  <p className="text-xs text-slate-500">Swipe</p>
                </button>
                <button
                  onClick={() => handleMethodSelect('usb')}
                  className="border-2 border-slate-200 rounded-lg p-4 hover:border-cyan-500 hover:bg-cyan-50 transition-all text-center"
                >
                  <Usb className="mx-auto text-cyan-600 mb-2" size={32} />
                  <p className="font-medium text-slate-900 text-sm">USB</p>
                  <p className="text-xs text-slate-500">Scanner</p>
                </button>
                <button
                  onClick={() => handleMethodSelect('camera')}
                  disabled={!barcodeScanningAvailable}
                  className={`border-2 rounded-lg p-4 transition-all text-center ${barcodeScanningAvailable ? 'border-slate-200 hover:border-cyan-500 hover:bg-cyan-50' : 'border-slate-100 bg-slate-50 opacity-50 cursor-not-allowed'}`}
                >
                  <Camera className={`mx-auto mb-2 ${barcodeScanningAvailable ? 'text-cyan-600' : 'text-slate-400'}`} size={32} />
                  <p className={`font-medium text-sm ${barcodeScanningAvailable ? 'text-slate-900' : 'text-slate-400'}`}>Camera</p>
                  <p className="text-xs text-slate-500">PDF417</p>
                </button>
              </div>
              <p className="text-xs text-center text-slate-500">Or close and enter manually</p>
            </div>
          )}

          {step === 'scanning' && selectedMethod === 'camera' && (
            <div className="py-2">
              <div className="mb-3">
                <div className="relative">
                  <video
                    ref={videoRef}
                    className="w-full rounded-lg border-2 border-slate-300 bg-black"
                    style={{ maxHeight: '350px' }}
                    autoPlay
                    playsInline
                  />
                  {scanStatus && (
                    <div className={`absolute top-2 left-2 right-2 ${scanStatus.includes('No barcode') || scanStatus.includes('Error') ? 'bg-amber-600' : 'bg-cyan-600'} text-white px-3 py-1.5 rounded text-xs font-medium text-center shadow-lg`}>
                      {scanStatus}
                    </div>
                  )}
                </div>
                <div className="mt-2">
                  <Button
                    onClick={captureAndDecode}
                    disabled={isSending}
                    size="lg"
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-5 text-base"
                  >
                    {isSending ? 'Processing...' : 'CAPTURE & SCAN'}
                  </Button>
                </div>
                <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-slate-700">
                  <p className="font-semibold text-amber-800 mb-1">Position the barcode inside the camera frame:</p>
                  <ul className="space-y-0.5">
                    <li>Flip ID to <strong>BACK</strong> — find the rectangular barcode</li>
                    <li>Hold the barcode <strong>close to the camera</strong> so it fills the rectangle</li>
                    <li>Make sure it is <strong>clear without any glare</strong></li>
                    <li><strong>Tilt slightly</strong> if you see reflection</li>
                    <li>Tap <strong>CAPTURE & SCAN</strong> when the barcode looks sharp</li>
                  </ul>
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={() => { stopCamera(); onClose(); }} variant="default" size="sm" className="flex-1">
                  Skip — Enter Manually
                </Button>
                <Button onClick={handleBack} variant="outline" size="sm" className="flex-1">
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
              </div>
            </div>
          )}

          {step === 'scanning' && selectedMethod !== 'camera' && (
            <div className="py-2">
              <div className="text-center mb-3">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-2 animate-pulse">
                  {selectedMethod === 'd5100' ? (
                    <Scan className="text-green-600" size={32} />
                  ) : selectedMethod === 'usb' ? (
                    <Usb className="text-green-600" size={32} />
                  ) : (
                    <CreditCard className="text-green-600" size={32} />
                  )}
                </div>
                <p className="text-base font-medium text-slate-900">
                  {selectedMethod === 'd5100' ? 'Tera D5100 Ready' :
                   selectedMethod === 'usb' ? 'USB Scanner Ready' :
                   'Mag Stripe Reader Ready'}
                </p>
                <p className="text-sm text-green-700 font-medium">
                  {selectedMethod === 'd5100' ? 'Scan the barcode on the back of the ID' :
                   selectedMethod === 'usb' ? 'Place ID and scan' :
                   'Swipe ID now'}
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded p-3 mb-3">
                <p className="text-xs font-semibold text-green-900 mb-1">Instructions:</p>
                <ol className="text-xs text-green-800 space-y-1">
                  {selectedMethod === 'd5100' ? (
                    <>
                      <li><strong>1.</strong> Flip ID to the <strong>BACK</strong> side</li>
                      <li><strong>2.</strong> Point the D5100 at the PDF417 barcode</li>
                      <li><strong>3.</strong> Press the scan button — data auto-fills</li>
                    </>
                  ) : selectedMethod === 'usb' ? (
                    <>
                      <li><strong>1.</strong> Place ID face-down on scanner</li>
                      <li><strong>2.</strong> Press scan button</li>
                      <li><strong>3.</strong> Data auto-populates</li>
                    </>
                  ) : (
                    <>
                      <li><strong>1.</strong> Swipe ID through reader</li>
                      <li><strong>2.</strong> One smooth motion</li>
                      <li><strong>3.</strong> Data auto-fills</li>
                    </>
                  )}
                </ol>
              </div>

              {scanStatus && (
                <div className={`mb-3 p-2 rounded text-xs font-medium text-center ${
                  scanStatus.includes('Could not') ? 'bg-amber-50 border border-amber-300 text-amber-800' :
                  'bg-cyan-50 border border-cyan-300 text-cyan-800'
                }`}>
                  {scanStatus}
                </div>
              )}

              <textarea
                ref={inputRef}
                value={scannedText}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                className="sr-only"
                autoFocus
              />

              {scannedText && (
                <div className="mb-3 p-2 bg-slate-100 rounded border text-xs font-mono max-h-20 overflow-auto">
                  Receiving data... ({scannedText.length} chars)
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={handleBack} variant="outline" size="sm" className="flex-1">
                  <ArrowLeft size={14} className="mr-1" /> Back
                </Button>
                <Button onClick={() => { stopCamera(); onClose(); }} variant="outline" size="sm" className="flex-1">
                  <X size={14} className="mr-1" /> Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IDScannerModal;
